import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Phone, Mail, Truck, Scissors } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO } from '../../data/mock';

/**
 * Header - completely new layout for Stitch Your Dash
 * - Top utility bar with contact + free shipping notice
 * - Main bar: Logo (left) | Underline Nav (center) | Account + Cart (right)
 * - Mobile slide-in panel
 */
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/shop', label: 'Shop' },
    { path: '/about', label: 'About' },
    { path: '/why-choose-us', label: 'Why Us' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      {/* Top utility strip */}
      <div className="hidden md:block bg-warmBrown-900 text-cream-100 text-[12px]">
        <div className="container-wide flex items-center justify-between h-9">
          <div className="flex items-center gap-6">
            <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-2 hover:text-rose-300 transition-colors">
              <Phone className="w-3.5 h-3.5" /> {CONTACT_INFO.phone}
            </a>
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-2 hover:text-rose-300 transition-colors">
              <Mail className="w-3.5 h-3.5" /> {CONTACT_INFO.email}
            </a>
          </div>
          <div className="flex items-center gap-2 text-cream-200">
            <Truck className="w-3.5 h-3.5 text-rose-300" />
            <span>Free Shipping on Orders $99+ · Ships to USA</span>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div
        className={`transition-all duration-300 ${
          isScrolled
            ? 'bg-warmWhite-100/92 backdrop-blur-md shadow-soft'
            : 'bg-warmWhite-100'
        }`}
      >
        <div className="container-wide">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileOpen(true)}
              className="lg:hidden -ml-2 p-2 text-warmBrown-900 hover:text-rose-600 transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <span className="flex flex-col leading-none">
                <span className="font-display text-[1.35rem] lg:text-2xl text-warmBrown-900 tracking-tight">
                  Stitch <em className="italic-display text-rose-600 not-italic" style={{fontStyle:'italic'}}>your</em> Dash
                </span>
                <span className="hidden sm:block text-[10px] uppercase tracking-[0.22em] text-warmBrown-500 mt-1 font-medium">
                  Sewing · Craft · Studio
                </span>
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-[14px] ${isActive(link.path) ? 'is-active' : ''}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={openCart}
                className="relative inline-flex items-center justify-center w-11 h-11 rounded-full bg-warmBrown-900 hover:bg-rose-600 text-white transition-all shadow-soft"
                data-testid="cart-button"
                aria-label="Open cart"
              >
                <ShoppingBag className="w-[18px] h-[18px]" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 text-[10px] font-bold flex items-center justify-center rounded-full bg-rose-500 text-white border-2 border-warmWhite-100">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="stitched-divider opacity-25" />
      </div>

      {/* Mobile slide-in panel */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMobileOpen ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
      >
        <div
          className="absolute inset-0 bg-warmBrown-950/40"
          onClick={() => setIsMobileOpen(false)}
        />
        <aside
          className={`absolute top-0 left-0 bottom-0 w-[86%] max-w-sm bg-warmWhite-100 shadow-elevated flex flex-col transform transition-transform duration-300 ${
            isMobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between p-5 border-b border-cream-200">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-xl text-warmBrown-900">
                Stitch <span className="text-rose-600 italic">your</span> Dash
              </span>
            </Link>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="p-2 text-warmBrown-700 hover:text-rose-600"
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <ul className="space-y-1">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`block px-4 py-3 rounded-2xl font-display text-lg transition-all ${
                      isActive(link.path)
                        ? 'bg-rose-50 text-rose-700'
                        : 'text-warmBrown-800 hover:bg-cream-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t border-cream-200 space-y-1">
              <p className="px-4 text-[11px] uppercase tracking-[0.2em] text-warmBrown-500 mb-2">More</p>
              {[
                { path: '/materials', label: 'Materials' },
                { path: '/how-to-use', label: 'How To Use' },
                { path: '/guarantee', label: 'Our Guarantee' },
                { path: '/shipping', label: 'Shipping' },
                { path: '/returns', label: 'Returns & Refunds' },
              ].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block px-4 py-2.5 rounded-xl text-warmBrown-700 hover:bg-cream-100"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>

          <div className="p-5 border-t border-cream-200 bg-cream-50 space-y-3 text-sm text-warmBrown-700">
            <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-2 hover:text-rose-600">
              <Phone className="w-4 h-4" /> {CONTACT_INFO.phone}
            </a>
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-2 hover:text-rose-600">
              <Mail className="w-4 h-4" /> {CONTACT_INFO.email}
            </a>
          </div>
        </aside>
      </div>
    </header>
  );
};

export default Header;
